import { useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useDispatch } from 'react-redux';

import { Box, Card, Image, Text } from '@zillow/constellation-10';

import { LoadingTag, LoadingText } from '@zg-rentals/loading-components';
import { AppSelectors, RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import PropertiesActions from '../../actions/PropertiesActions';

import {
  syncOnDataChangePropertyDetailsOverview,
  useGetPropertyDetailsOverview,
} from '../../hooks/queries/useGetPropertyDetailsOverview';

import { syncRecommendations, useGetListingRecommendations } from '../../hooks/queries/recommendations';

import { syncCurrentProperty, useGetListingSummary } from '../../hooks/queries/summary';
// context
import { DashboardContext } from '../../containers/DashboardPage/context';
import { FreemiumContext } from '../Freemium/context';
import { PaymentGatewayContext } from '../../components/PaymentGateway/context';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';
import { updateLoadingState } from '../../providers/LoadingMapContext/actions';

import accessUtils from '../../utils/accessUtils';

import propertyUtils from '../../utils/propertyUtils';
import { getTagTypeByKey, STATUS_KEY } from '../../utils/statusTagUtils';

import { RELISTING_STEP_SUFFIX, STEP, STEP_DELIMITER } from '../../containers/CreateListingPage/constants';

import WebSocketMessageNameConstants from '../../constants/WebSocketMessageNameConstants';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../constants/FreemiumConstants';
import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../constants/ListingSummaryConstants';

import Syndication from '../../model/Syndication';

import ConnectedModuleCard from '../ConnectedModuleCard';
import ConnectedNetworkView, { DISPLAY_TYPE } from '../../components/ConnectedNetworkView';

import { LAUNCHED_FROM_OPTIONS } from '../../components/Modal/DeactivationModal/constants';
import FeedListingDeactivateModal from '../../components/Modal/FeedListingDeactivateModal';

import DeactivateListingModal from '../Modal/DeactivateListingModal';

import PremiumBadge from '../../images/premium-badge.png';
import ListingCompletenessScore from '../ListingCompletenessScore';

// sub components
import Alert from './components/Alert';
import CTA from './components/CTA';
import Comps from './components/Comps';
import Details from './components/Details';
import ProgressStepperSummary from './components/ProgressStepperSummary';
import Statistics from './components/Statistics';
import PremiumCTA from './components/PremiumCTA';
import PremiumUpgrade from './components/PremiumUpgrade';
import ListingAvailability from './components/ListingAvailability';
import PremiumStatistics from './components/Statistics/PremiumStatistics';

import { flexStyles } from './styles';

import type { State as DashboardState } from '../../containers/DashboardPage/reducer';
import type { ListingSummaryResponse } from '../../hooks/queries/summary/types';

type Props = {
  updateMinModuleHeight?: () => void;
};

type Message = {
  data: string;
};

const ConnectedListing = ({ updateMinModuleHeight }: Props) => {
  const {
    pgState: { isResponseSuccess },
  } = useContext(PaymentGatewayContext);

  const {
    freemiumState: { canAccessTopOfFunnelPremium },
  } = useContext(FreemiumContext);

  const { dashboardState } = useContext(DashboardContext);
  const { fluidSmallLTE }: Partial<DashboardState> = dashboardState;

  const {
    loadingByComponent: { ConnectedListing: isConnectedListingLoading },
    reducerDispatch,
  } = useContext(LoadingMapContext);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    user,
    properties: {
      propertyOverview: {
        listingAvailability: { shouldCheckIn },
      },
      currentProperty,
    },
  } = useSelector((state) => state);

  const makeIsScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));

  const [displayDeactivateModal, setDisplayDeactivateModal] = useState(false);
  const [displayFeedListingDeactivationModal, setShowFeedListingDeactivateModal] = useState(false);
  // this component always hides border when fluid is true for less than or equal to small screen sizes
  const [hideBorder, setHideBorder] = useState(fluidSmallLTE);

  const {
    data: listingSummaryData = {} as ListingSummaryResponse,
    data: {
      rentZestimate = 0,
      canDeactivate = false,
      modularListingProgress = {} as ListingSummaryResponse['modularListingProgress'],
    } = {} as ListingSummaryResponse,
    isPending: isListingSummaryLoading,
    error: listingSummaryError,
  } = useGetListingSummary(listingAlias);

  const { data: propertyDetailsOverviewData, isLoading: isPropertyDetailsOverviewLoading } =
    useGetPropertyDetailsOverview(listingAlias);

  const {
    data: listingRecommendationsData,
    isPending: isListingRecsLoading,
    error: listingRecsError,
  } = useGetListingRecommendations({
    listingAlias,
  });

  const {
    hasAnalyticsRentComps,
    isActive: active,
    isFeed,
    dateActivatedTimestamp: dateLastActivatedTimestamp,
    dateExpirationTimestamp: expirationDateTimestamp,
    syndication: { overview: { displayStatus = '' } = {} } = {},
  } = listingSummaryData;

  const status = displayStatus as keyof typeof LISTING_SUMMARY_DISPLAY_STATUS;

  const lowerCaseStatus = status?.toLowerCase();

  const isRequiredDataEmpty = lowerCaseStatus === '';
  const isModuleCardLoading = isListingSummaryLoading || isListingRecsLoading || isRequiredDataEmpty;

  // @ts-expect-error
  const isFreemiumEnabled = propertyDetailsOverviewData?.freemium?.isEnabled;

  const canAccessModularListingCreation = accessUtils.canAccessModularListing({
    currentProperty: {
      ...currentProperty,
      // use more up to date dateLastActivatedTimestamp value from listingSummaryData
      dateLastActivatedTimestamp,
    },
    user,
  });

  useEffect(() => {
    updateLoadingState({
      reducerDispatch,
      data: {
        propertyDetailsOverview: isPropertyDetailsOverviewLoading,
        listingRecommendations: isListingRecsLoading,
      },
    });
  }, [isPropertyDetailsOverviewLoading, isListingRecsLoading]);

  useEffect(() => {
    if (updateMinModuleHeight) {
      updateMinModuleHeight();
    }

    window.rentalManagerApiWebSocket.addEventListener('message', handleRentalManagerApiWebSocket);

    return () => window.rentalManagerApiWebSocket.removeEventListener('message', handleRentalManagerApiWebSocket);
  }, []);

  useEffect(() => {
    if (Object.keys(listingSummaryData).length > 0) {
      syncCurrentProperty({ data: listingSummaryData, dispatch });
    }
  }, [listingSummaryData]);

  useEffect(() => {
    if (propertyDetailsOverviewData) {
      syncOnDataChangePropertyDetailsOverview({ data: propertyDetailsOverviewData, currentProperty, dispatch });
    }
  }, [propertyDetailsOverviewData]);

  useEffect(() => {
    if (listingRecommendationsData) {
      syncRecommendations({
        data: listingRecommendationsData,
        isMultifamily: currentProperty?.isMultifamily,
        dispatch,
      });
    }
  }, [listingRecommendationsData, currentProperty]);

  useEffect(() => {
    if (isResponseSuccess) {
      queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
    }
  }, [isResponseSuccess]);

  useEffect(() => {
    if (fluidSmallLTE !== undefined) {
      setHideBorder(fluidSmallLTE);
    }
  }, [fluidSmallLTE]);

  const handleRentalManagerApiWebSocket = (message: Message) => {
    // update listing status section with syndication status updates.
    let data;

    try {
      data = JSON.parse(message.data);
    } catch (error) {
      return;
    }

    if (data.name !== WebSocketMessageNameConstants.LISTING_SYNDICATION_STATUS) {
      return;
    }

    const { payload } = data;
    const { feedId, feedListingId, listingAlias: messageListingAlias } = payload;

    // Don't do anything if the message is for a different listing
    if (listingAlias !== messageListingAlias && listingAlias !== propertyUtils.getListingId(feedId, feedListingId)) {
      return;
    }

    const syndicationPayload = payload.userPropertyDetailsSyndicationDto;
    const syndicationCopy = { ...currentProperty?.syndication };

    syndicationCopy.hotpads = syndicationPayload.hotpads;
    syndicationCopy.trulia = syndicationPayload.trulia;
    syndicationCopy.zillow = syndicationPayload.zillow;

    // external
    syndicationCopy.realtor = syndicationPayload.realtor;
    syndicationCopy.rent = syndicationPayload.rent;
    syndicationCopy.redfin = syndicationPayload.redfin;
    syndicationCopy.apartmentGuide = syndicationPayload.apartmentGuide;

    syndicationCopy.universal = syndicationPayload.universal;
    syndicationCopy.overview = syndicationPayload.overview;
    syndicationCopy.customerProblems = syndicationPayload.customerProblems;

    dispatch(PropertiesActions.updateCurrentProperty({ syndication: Syndication(syndicationCopy) }));
  };

  if (listingSummaryError) {
    return (
      // @ts-expect-error
      <ConnectedModuleCard title="Listing" isLoading={false} size="sm">
        <Box css={flexStyles}>{listingSummaryError?.error?.message}</Box>
      </ConnectedModuleCard>
    );
  }

  const isActiveProductTypePremium =
    // @ts-expect-error
    propertyDetailsOverviewData?.freemium?.activeProduct?.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const isActiveProductTypeFree =
    // @ts-expect-error
    propertyDetailsOverviewData?.freemium?.activeProduct?.type === PRODUCT_TYPE_NAME_TO_ID.FREE;

  // @ts-expect-error
  const activeProductExpirationTimestamp = propertyDetailsOverviewData?.freemium?.activeProduct?.expirationTimestamp;

  const includePremiumBorderTop = isFreemiumEnabled && isActiveProductTypePremium;

  /**
   * for premium listing we want to display the top blue border
   * we need to bypass the hide border completely because the default value is grey
   */
  const bypassHideBorder = includePremiumBorderTop && !makeIsScreenMobile;

  const shouldHideBorderOnMobile = bypassHideBorder ? false : hideBorder;

  let { step } = modularListingProgress || {};
  if (step === `${STEP.REVIEW}${STEP_DELIMITER}${RELISTING_STEP_SUFFIX}`.toLowerCase()) {
    step = step.split(STEP_DELIMITER)[0];
  }
  const canResumeModularListingCreation = canAccessModularListingCreation && step !== undefined;

  const handleDeactivateClick = () => {
    if (isFeed) {
      return setShowFeedListingDeactivateModal(true);
    }

    return setDisplayDeactivateModal(true);
  };

  const renderTag = () => (
    <LoadingTag
      tone={isConnectedListingLoading ? 'gray' : getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, status)}
      size="sm"
      isLoading={isConnectedListingLoading}
      loadingWidth="7rem"
      css={{
        textTransform: 'uppercase',
      }}
    >
      {status}
    </LoadingTag>
  );

  const renderVON = () => {
    if (
      lowerCaseStatus === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE ||
      lowerCaseStatus === LISTING_SUMMARY_DISPLAY_STATUS.DECLINED ||
      isPropertyDetailsOverviewLoading
    ) {
      return null;
    }

    return (
      <ConnectedNetworkView
        label="View your listing"
        size="sm"
        status={status}
        syndication={currentProperty.syndication}
        displayType={DISPLAY_TYPE.DROPDOWN_BUTTON}
        isLoading={isConnectedListingLoading}
        useFixedWidth={true}
      />
    );
  };

  // render comps or alert depending on the status
  const renderByStatus = () => {
    if (isConnectedListingLoading) {
      return (
        <LoadingText isLoading={true} loadingWidth="100%">
          &nbsp;
        </LoadingText>
      );
    }

    switch (lowerCaseStatus) {
      case LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE: {
        if (canResumeModularListingCreation) {
          return <ProgressStepperSummary status={status} step={step} />;
        }

        return <Comps status={status} hasAnalyticsRentComps={hasAnalyticsRentComps} />;
      }

      case LISTING_SUMMARY_DISPLAY_STATUS.PARTIALLY_PUBLISHED:
      case LISTING_SUMMARY_DISPLAY_STATUS.DECLINED:
        return <Alert />;
      case LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHED:
      case LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHING:
        return isFreemiumEnabled && isActiveProductTypePremium ? (
          <PremiumStatistics />
        ) : (
          <Statistics hasAnalyticsRentComps={hasAnalyticsRentComps} status={status} />
        );
      default:
        return null;
    }
  };

  const renderLeftComponent = () => {
    return (
      <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        {isFreemiumEnabled && isActiveProductTypePremium && (
          <Image src={PremiumBadge} alt="premium badge" css={{ width: '35px' }} />
        )}
        <Text
          asChild
          css={{
            textStyle: { base: 'body-bold', lg: 'heading-xs' },
            marginRight: { base: '200', lg: '400' },
          }}
        >
          <h2>{renderModuleCardHeading()}</h2>
        </Text>

        {renderTag()}
      </Box>
    );
  };

  const renderModuleCardHeading = () => {
    if (isFreemiumEnabled && isActiveProductTypePremium) {
      return 'Premium listing';
    }

    if (isFreemiumEnabled && active && isActiveProductTypeFree) {
      return 'Basic listing';
    }

    return 'Listing';
  };

  const displayPremiumCTA = (isFreemiumEnabled && active) || (canAccessTopOfFunnelPremium && !isActiveProductTypeFree);

  return (
    <div data-testid="ConnectedListing">
      <ConnectedModuleCard
        titleGrow={0}
        includeBorderTop={isFreemiumEnabled && isActiveProductTypePremium}
        justifyHeadingContent={isFreemiumEnabled && isActiveProductTypePremium && 'space-between'}
        hideBorderOnMobile={shouldHideBorderOnMobile}
      >
        {/**
         * do not use ConnectedModuleCard heading section
         * render custom header section because the flex break points are custom with multiple flex containers
         **/}
        <Box
          css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' }, justifyContent: 'space-between' }}
          data-testid="ConnectedListing-Heading"
        >
          {renderLeftComponent()}

          <Box css={{ marginBottom: { base: '400', lg: 0 } }} />

          {renderVON()}
        </Box>

        <Box css={{ marginBottom: { base: '400', lg: 'md' } }} />

        <Box css={flexStyles}>
          {expirationDateTimestamp && shouldCheckIn && !isConnectedListingLoading ? (
            <Box css={{ marginBottom: '600' }}>
              <ListingAvailability expirationDateTimestamp={expirationDateTimestamp} />
            </Box>
          ) : null}

          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0,1fr))', gap: '400' }}>
            <Box
              data-testid="Grid-DetailsContainer"
              css={{ gridColumnEnd: { base: 'span 12', xl: !canResumeModularListingCreation ? 'span 7' : 'span 12' } }}
            >
              <Card tone="neutral" elevated={false} css={{ minHeight: '200px' }}>
                <Details rentZestimate={rentZestimate} />
                <Box
                  css={{
                    flexGrow: 0,
                    marginBottom: '15px',
                    borderBottom: '1px solid {colors.border.muted}',
                    borderRight: 'none',
                  }}
                />
                <Box css={{ minHeight: '60px' }}>{renderByStatus()}</Box>

                {canResumeModularListingCreation && (
                  <Box css={{ marginBlock: '400', display: 'flex', justifyContent: 'center' }}>
                    <CTA
                      canDeactivate={canDeactivate}
                      listingAlias={listingAlias}
                      canResumeModularListingCreation={canResumeModularListingCreation}
                      status={lowerCaseStatus}
                      onDeactivateClick={handleDeactivateClick}
                    />
                  </Box>
                )}
                {displayPremiumCTA && !isActiveProductTypePremium && (
                  <PremiumCTA isActiveProductTypeFree={isActiveProductTypeFree} />
                )}
              </Card>
            </Box>

            <Box
              data-testid="Grid-ListingCompletenessScoreContainer"
              css={{ gridColumnEnd: { base: 'span 12', xl: 'span 5' } }}
            >
              {!canResumeModularListingCreation && (
                <Card tone="neutral" elevated={false} css={{ minHeight: '200px' }}>
                  <ListingCompletenessScore
                    isFromSOH={true}
                    canResumeModularListingCreation={canResumeModularListingCreation}
                  />
                  <Box css={{ marginTop: '400', marginBottom: '600' }}>
                    <CTA
                      canDeactivate={canDeactivate}
                      listingAlias={listingAlias}
                      canResumeModularListingCreation={canResumeModularListingCreation}
                      status={lowerCaseStatus}
                      onDeactivateClick={handleDeactivateClick}
                    />
                  </Box>
                  {!listingRecsError && isActiveProductTypeFree && isFreemiumEnabled && active && <PremiumUpgrade />}
                </Card>
              )}
            </Box>
          </Box>

          {displayPremiumCTA && isActiveProductTypePremium && (
            <PremiumCTA
              isActiveProductTypeFree={isActiveProductTypeFree}
              activeProductExpirationTimestamp={activeProductExpirationTimestamp}
            />
          )}
        </Box>
      </ConnectedModuleCard>
      {displayDeactivateModal && (
        <DeactivateListingModal open={displayDeactivateModal} onOpenChange={setDisplayDeactivateModal} />
      )}
      {displayFeedListingDeactivationModal && (
        <FeedListingDeactivateModal
          setShowFeedListingDeactivateModal={setShowFeedListingDeactivateModal}
          launchedFrom={LAUNCHED_FROM_OPTIONS.SOH}
        />
      )}
    </div>
  );
};

export default ConnectedListing;
